<template>
  <v-dialog v-model="dialog" max-width="560">
    <v-card class="pa-4">
      <v-card-title class="pa-0 ma-0 align-center d-flex justify-space-between">
        <span class="text-h5">Delete your account?</span>
        <v-btn variant="text" icon color="grey" :disabled="requesting" @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pa-0 mt-6">
        <p class="mb-4">
          We will delete your account, your login, your profile, and every project you own with all its workflows.
        </p>

        <v-progress-linear v-if="checkingProjects" indeterminate color="primary" class="mb-4" />
        <v-alert
          v-else-if="sharedProjectNames.length"
          type="warning"
          variant="tonal"
          density="compact"
          class="mb-4"
          data-cy="account-deletion-shared-projects"
        >
          These projects are shared with other people: {{ sharedProjectNames.join(', ') }}.
          Deleting your account deletes them and all their workflows for everyone.
          To keep them, make another member the owner of each project first.
        </v-alert>
        <v-alert v-else-if="projectCheckFailed" type="warning" variant="tonal" density="compact" class="mb-4">
          We could not check which of your projects are shared. If other people are members of a project you own,
          deleting your account deletes it for them too.
        </v-alert>

        <p class="mb-1">We keep:</p>
        <ul class="kept-list mb-4">
          <li>your invoices, at Stripe, because bookkeeping law requires it</li>
          <li>your comments, reactions and attachments in other people's workflows, shown as written by "{{ DELETED_USER_NAME }}"</li>
          <li>your name in the version history of workflows you edited in other people's projects</li>
        </ul>

        <p>Our team deletes your account within one month and emails you when it is done.</p>
      </v-card-text>
      <v-card-actions class="pa-0 mt-6 justify-end">
        <v-btn size="large" variant="outlined" :disabled="requesting" @click="dialog = false">
          Cancel
        </v-btn>
        <v-btn
          size="large"
          variant="flat"
          color="primary"
          :loading="requesting"
          :disabled="checkingProjects"
          data-cy="account-deletion-confirm-btn"
          @click="requestDeletion"
        >
          Request deletion
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useStore } from 'vuex'
import client from '@/graphql/client'
import ListTeamMembers from '@/graphql/queries/ListTeamMembers'
import RequestAccountDeletion from '@/graphql/mutations/RequestAccountDeletion'
import { DELETED_USER_NAME } from '@/utils/constants'

const dialog = defineModel({ type: Boolean, default: false })
const emit = defineEmits(['requested'])

const store = useStore()

const checkingProjects = ref(false)
const projectCheckFailed = ref(false)
const sharedProjectNames = ref([])
const requesting = ref(false)

const hasOtherMembers = async (projectId, userId) => {
  let nextToken = null

  do {
    const res = await client.query({
      operationName: 'listTeamMembers',
      query: ListTeamMembers,
      variables: { projectId, limit: 100, nextToken }
    })
    const data = res?.listTeamMembers

    if (data?.items?.some((member) => member.userId !== userId)) {
      return true
    }

    nextToken = data?.nextToken
  } while (nextToken)

  return false
}

const findSharedProjectNames = async () => {
  const userId = store.state.userId
  const ownedProjects = (store.state.projectMemberships.projectMemberships || [])
    .map((membership) => membership.project)
    .filter((project) => project?.organisation?.userId === userId)
  const shared = await Promise.all(ownedProjects.map((project) => hasOtherMembers(project.id, userId)))

  return ownedProjects.filter((project, index) => shared[index]).map((project) => project.name)
}

const checkSharedProjects = async () => {
  checkingProjects.value = true
  projectCheckFailed.value = false
  sharedProjectNames.value = []

  try {
    sharedProjectNames.value = await findSharedProjectNames()
  } catch (error) {
    projectCheckFailed.value = true
  } finally {
    checkingProjects.value = false
  }
}

const requestDeletion = async () => {
  requesting.value = true

  try {
    const res = await client.query({ operationName: 'requestAccountDeletion', query: RequestAccountDeletion })
    emit('requested', res.requestAccountDeletion.requestedAt)
    dialog.value = false
  } catch (error) {
    store.commit('snackbar/showMessage', {
      content: 'Could not request the deletion. Please try again.',
      color: 'red'
    }, { root: true })
  } finally {
    requesting.value = false
  }
}

watch(dialog, (open) => {
  if (open) {
    checkSharedProjects()
  }
})
</script>

<style scoped>
.kept-list {
  padding-left: 20px;
}
</style>
